export type GooglePlacesMode = 'cities' | 'address';

/** Details from a selected Places API (New) Place, after fetchFields. */
export type GooglePlacesSelection = {
  displayName?: string;
  formattedAddress?: string;
  id?: string;
  location?: google.maps.LatLng | null;
  /** ISO 3166-1 alpha-2 country code extracted from addressComponents, e.g. "NL". */
  country_code?: string;
};

export const CITIES_INCLUDED_PRIMARY_TYPES = [
  'locality',
  'administrative_area_level_3',
] as const;

export function buildAutocompleteRequest(
  input: string,
  mode: GooglePlacesMode,
  sessionToken: google.maps.places.AutocompleteSessionToken,
): google.maps.places.AutocompleteRequest {
  const request: google.maps.places.AutocompleteRequest = {
    input,
    sessionToken,
  };
  if (mode === 'cities') {
    request.includedPrimaryTypes = [...CITIES_INCLUDED_PRIMARY_TYPES];
  }
  return request;
}

export function predictionLabel(
  prediction: google.maps.places.PlacePrediction,
): string {
  return prediction.text?.toString() ?? prediction.mainText?.toString() ?? '';
}

export function placeToFormValue(place: {
  displayName?: string | null;
  formattedAddress?: string | null;
}): string {
  return place.displayName || place.formattedAddress || '';
}

/**
 * Extract the ISO 3166-1 alpha-2 country code from a Place's addressComponents.
 * Returns undefined when the country component is absent.
 */
export function extractCountryCode(
  addressComponents: google.maps.places.AddressComponent[] | null | undefined,
): string | undefined {
  if (!addressComponents) return undefined;
  const countryComponent = addressComponents.find((c) =>
    c.types.includes('country'),
  );
  return countryComponent?.shortText ?? undefined;
}

export function toPlacesSelection(place: {
  displayName?: string | null;
  formattedAddress?: string | null;
  id?: string | null;
  location?: google.maps.LatLng | null;
  addressComponents?: google.maps.places.AddressComponent[] | null;
}): GooglePlacesSelection {
  return {
    displayName: place.displayName ?? undefined,
    formattedAddress: place.formattedAddress ?? undefined,
    id: place.id ?? undefined,
    location: place.location ?? null,
    country_code: extractCountryCode(place.addressComponents),
  };
}
